/* eslint-disable no-continue */
import { Form, Formik, useFormik } from "formik";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { format, startOfDay } from "date-fns";
import _ from "lodash";
import { Icon } from "@iconify/react";
import { Input, Label } from "../components/shared/InputField";
import SeparationReasons from "../constants/SeparationReasons";
import {
  ButtonPrimary,
  ButtonPrimary1,
  Error,
  HStack,
  Notes,
  VStack,
} from "../components/utils";
import { Select } from "../components/shared/Select";
import { getDefaultResignationProcessObject } from "../pages/employeeDetails/Resignation";
import {
  useEmployeePageDetails,
  useGetResignationDetails,
  useResignationProcess,
} from "../queries/employees";
import { useError } from "../store/errorStore";
import { useGrants, useVestingTables } from "../queries";
import { useGetEsopPlanDetails } from "../queries/esopPlan";
import { ForfeitureSummaryCard } from "../components/shared/ForfeitureSummaryCard";
import { ExitDateBasis } from "../types/EsopPlan";

import { SwitchButton } from "../components/shared/SwitchButton";
import { useGetGrantLetterSettings } from "../queries/siteSettings";
import { formatDate, formatDisplayDate, parseDateString } from "../utils/date";
import StatusLabel from "../pages/esopOverview/StatusLabel";

type InitiateResignationType = {
  onClose: () => void;
  onPrimaryAction: () => void;
  dateOfJoining: Date;
};

interface InitiateResignationValues {
  resignationDate: string;
  resignationReason: string;
  lastWorkingDate: string;
}

function PlanForfeitureSummary({
  planId,
  planName,
  separationReason,
  lastDay,
  resignationDate,
  dateOfJoining,
}: {
  planId: string;
  planName: string;
  separationReason: string;
  lastDay?: Date;
  resignationDate?: Date;
  dateOfJoining?: Date;
}) {
  const { data: plan } = useGetEsopPlanDetails(planId, !!planId);
  if (!plan?.forfeitureSettings) return null;
  // Only show here when the plan uses resignation date as the basis
  if (plan.forfeitureSettings.exitDateBasis !== ExitDateBasis.RESIGNATION_DATE)
    return null;
  return (
    <ForfeitureSummaryCard
      forfeitureSettings={plan.forfeitureSettings}
      separationReason={separationReason}
      lastDay={lastDay}
      resignationDate={resignationDate}
      planName={planName}
      dateOfJoining={dateOfJoining}
    />
  );
}

export function InitiateResignation(props: InitiateResignationType) {
  const { id } = useParams();
  const _id = id ?? "";
  const { onClose, onPrimaryAction } = props;
  const { data: resignationDetails, refetch: getResignationDetails } =
    useGetResignationDetails(_id);
  const intialValues = {
    resignationDate:
      resignationDetails?.initiationDetails.resignationDate || "",
    resignationReason:
      resignationDetails?.initiationDetails.resignationReason || "",
    lastWorkingDate:
      resignationDetails?.lastWorkingDayDetails?.lastWorkingDate || "",
  };
  const validationSchema = Yup.object().shape({
    resignationDate: Yup.string()
      .required("Resignation Date is required")
      .test(
        "is-after-date-of-joining",
        `Resignation date should be on or after the date of joining (${formatDisplayDate(
          props.dateOfJoining
        )})`,
        (value) => {
          if (!value) return false;
          const resignationDate = new Date(value);
          const dateOfJoining = new Date(props.dateOfJoining);
          return startOfDay(resignationDate) >= startOfDay(dateOfJoining);
        }
      ),
    resignationReason: Yup.string().required("Resignation reason is required"),
  });

  const formik = useFormik({
    initialValues: intialValues,
    validationSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const resignationDto = getDefaultResignationProcessObject(_id);
  const { data: employee } = useEmployeePageDetails(_id);
  const { data: grantSettings } = useGetGrantLetterSettings();
  const { data: _grants, isFetched } = useGrants({ employeeId: _id });
  const grantsData = _grants?.grants || [];
  const { data: vestingData } = useVestingTables(_id);
  const grantsForEmployee = grantsData?.filter(
    (grant) =>
      grant.employeeId === _id && grant.optionHolderState !== "OFFER_EXPIRED"
  );

  const uniquePlanIds = useMemo(() => {
    if (!grantsForEmployee) return [];
    const seen = new Set<string>();
    const result: { planId: string; planName: string }[] = [];
    for (const grant of grantsForEmployee) {
      if (!seen.has(grant.planid)) {
        seen.add(grant.planid);
        result.push({ planId: grant.planid, planName: grant.planName });
      }
    }
    return result;
  }, [grantsForEmployee]);
  const hasNonGrantedGrants = grantsForEmployee?.some(
    (grant) =>
      grant.optionHolderState !== "GRANTED" &&
      grant.optionHolderState !== "RESIGNED"
  );
  const nonGrantedGrants = grantsForEmployee?.filter(
    (grant) =>
      grant.optionHolderState !== "GRANTED" &&
      grant.optionHolderState !== "OFFERED" &&
      grant.optionHolderState !== "WITHDRAW"
  );
  const grantsInOfferedState = grantsForEmployee?.filter(
    (grant) => grant.optionHolderState === "OFFERED"
  );
  const withdrawnGrants = grantsForEmployee?.filter(
    (grant) => grant.optionHolderState === "WITHDRAW"
  );
  const willgrantsBePasuedOnResignation = useMemo(
    () => grantSettings?.autoPauseVestingOnResignation,
    [
      grantSettings,
      resignationDetails?.pauseAndRevertOnResignationDate,
      isFetched,
    ]
  );

  const willOfferedGrantsBeDeleted = useMemo(
    () => grantSettings?.discardOfferedGrantsOnResignation,
    [
      grantSettings,
      resignationDetails?.pauseAndRevertOnResignationDate,
      isFetched,
    ]
  );

  const [deleteWithdrawnGrants, setDeleteWithdrawnGrants] = useState(true);
  const grantsToBeDeleted = useMemo(
    () => [
      ...(nonGrantedGrants || []),
      ...(willOfferedGrantsBeDeleted ? grantsInOfferedState || [] : []),
      ...(deleteWithdrawnGrants ? withdrawnGrants || [] : []),
    ],
    [
      deleteWithdrawnGrants,
      willOfferedGrantsBeDeleted,
      resignationDetails?.pauseAndRevertOnResignationDate,
      isFetched,
      withdrawnGrants,
    ]
  );

  const errorMessage = useError();

  const [toggleDeleteGrants, setToggleDeleteGrants] = useState(false);

  useEffect(() => {
    if (
      resignationDetails?.initiationDetails?.deleteWithdrawnGrants !== undefined
    ) {
      setDeleteWithdrawnGrants(
        Boolean(resignationDetails.initiationDetails.deleteWithdrawnGrants)
      );
    }
  }, [resignationDetails?.initiationDetails?.deleteWithdrawnGrants]);

  const { mutate: resignationProcess } = useResignationProcess();

  const handleSubmit = (values: InitiateResignationValues) => {
    resignationDto.initiationDetails.employeeId = _id;
    resignationDto.initiationDetails.resignationDate = values.resignationDate;
    resignationDto.initiationDetails.resignationReason =
      (values as InitiateResignationValues).resignationReason || "";
    resignationDto.initiationDetails.saveDetails = true;
    resignationDto.initiationDetails.deleteWithdrawnGrants =
      deleteWithdrawnGrants;
    resignationDto.lastWorkingDayDetails = {
      setLastWorkingDay: !!values.lastWorkingDate,
      lastWorkingDate: values.lastWorkingDate || "",
      lapsedImmediately: true,
    };

    if (error || error === "" || error === "undefined" || error === "null") {
      toast(error, {
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    resignationProcess(resignationDto, {
      onSuccess: () => {
        toast("Initiated Resignation", {
          type: "success",
          autoClose: 2000,
        });
        getResignationDetails();
        onPrimaryAction();
      },
      onError: (err: any) => {
        const errMsg = err.response?.data?.errorsValidation?.errorRaw;
        if (errMsg) {
          errorMessage.setMessage(errMsg);
          toast(errMsg, {
            type: "error",
            autoClose: 2000,
          });
        }
      },
    });
  };

  const [isConfirmed, setIsConfirmed] = useState(false);

  const [confirmationInput, setConfirmationInput] = useState("");

  const [latestVestingDate, setLatestVestingDate] = useState<Date>(new Date());

  const [error, setError] = useState<string | null>(null);

  const [revertingData, setRevertingData] = useState<
    { identifier: string; options: number; grantDate?: Date }[]
  >([]);

  const useLastWorkingDayForLapsing =
    grantSettings?.dateForLapsing !== "resignationDate";
  const reversionCutoffDate = useLastWorkingDayForLapsing
    ? formik.values.lastWorkingDate
    : formik.values.resignationDate;
  const reversionCutoffLabel = useLastWorkingDayForLapsing
    ? "Last Working Date"
    : "resignation date";

  useEffect(() => {
    if (vestingData && vestingData?.length > 0) {
      const latestDate = vestingData
        .filter((vesting) => vesting.transactionType === "VESTED")
        .reduce((latest: Date, vesting) => {
          const date = new Date(vesting.date);
          return date > latest ? date : latest;
        }, new Date(0));
      setLatestVestingDate(latestDate);
    }
  }, [vestingData]);

  useEffect(() => {
    setRevertingData([]);
    setError(null);
    setIsConfirmed(false);
    if (vestingData && vestingData.length > 0 && reversionCutoffDate) {
      const groupedData = _.groupBy(vestingData, (v) => v.identifier);

      Object.entries(groupedData).forEach(([grantId, transactions]) => {
        const vestedOptions = _.sumBy(
          transactions.filter((t) => t.transactionType === "VESTED"),
          (et) => et.options
        );

        const exercisedAndForfeitedOptions = _.sumBy(
          transactions.filter(
            (t) =>
              t.transactionType === "CASHED_OUT" ||
              t.transactionType === "STOCK_EXERCISE" ||
              t.transactionType === "SURRENDERED" ||
              t.transactionType === "CASH_EXERCISE_REQUESTED" ||
              t.transactionType === "STOCK_EXERCISE_REQUESTED"
          ),
          (et) => et.options
        );

        let totalRevertingInGrant = 0;

        transactions.forEach((pv) => {
          if (
            pv.transactionType === "VESTED" &&
            new Date(pv.date) > new Date(reversionCutoffDate)
          ) {
            totalRevertingInGrant += pv.options;
          }
        });

        if (totalRevertingInGrant > 0) {
          if (
            totalRevertingInGrant >
            vestedOptions - exercisedAndForfeitedOptions
          ) {
            setError(
              `The Grant with Id ${grantId} has more exercised and forfeited options than Reverted Vested options. Please check the data.`
            );
          }
          const grant = grantsData?.find((g) => g.grantIdentifier === grantId);
          setRevertingData((prev) => [
            ...prev,
            {
              identifier: grant?.grantIdentifier || "",
              options: totalRevertingInGrant,
              grantDate: new Date(grant?.grantDate || ""),
            },
          ]);
        }
      });
    }
  }, [
    formik.values.resignationDate,
    formik.values.lastWorkingDate,
    grantSettings?.dateForLapsing,
  ]);
  return (
    <>
      <div className="">
        <div className="px-10 text-lg font-medium border-b py-7">
          <h6 className="flex justify-between">
            Initiate Resignation{" "}
            <span onClick={() => props.onClose()} className="cursor-pointer">
              X
            </span>{" "}
          </h6>
        </div>
        <VStack className="w-full px-10 py-7 gap-11">
          <form onSubmit={formik.handleSubmit}>
            <VStack className="flex-wrap gap-4">
              <div className="py-2">
                You are starting the resignation process for Employee, please
                enter the resignation date to continue.
              </div>
              <HStack className="items-start w-full">
                <Label className="required w-48 text-sm font-normal whitespace-nowrap">
                  Resignation Date
                </Label>
                <VStack className="gap-1 flex-1">
                  <Input
                    type="date"
                    className="w-full max-w-md"
                    min={formatDate(props.dateOfJoining)}
                    {...formik.getFieldProps("resignationDate")}
                  />
                  {formik.touched.resignationDate &&
                    formik.errors.resignationDate && (
                      <Error text={String(formik.errors.resignationDate)} />
                    )}
                </VStack>
              </HStack>
              <HStack className="items-start w-full">
                <Label className="required w-48 text-sm font-normal whitespace-nowrap">
                  Reason for resignation
                </Label>
                <VStack className="gap-1 flex-1">
                  <Select
                    options={SeparationReasons}
                    isPlaceHolderDisabled={true}
                    placeholder="Select reason"
                    valueGetter={(o: any) => o.value}
                    textGetter={(o: any) => o.label}
                    className="w-full p-3 max-w-md"
                    {...formik.getFieldProps("resignationReason")}
                  />
                  {formik.touched.resignationReason &&
                    formik.errors.resignationReason && (
                      <Error text={String(formik.errors.resignationReason)} />
                    )}
                </VStack>
              </HStack>
              <HStack className="items-start w-full">
                <Label
                  className={`w-48 text-sm font-normal whitespace-nowrap ${
                    grantSettings?.dateForLapsing === "lastWorkingDay" ||
                    !grantSettings?.dateForLapsing
                      ? "required"
                      : ""
                  }`}
                >
                  Last Working Day
                </Label>
                <VStack className="gap-1 flex-1">
                  <Input
                    type="date"
                    className="w-full max-w-md"
                    min={
                      formik.values.resignationDate ||
                      formatDate(props.dateOfJoining)
                    }
                    {...formik.getFieldProps("lastWorkingDate")}
                  />
                </VStack>
              </HStack>
              {(grantSettings?.dateForLapsing === "lastWorkingDay" ||
                !grantSettings?.dateForLapsing) &&
                !formik.values.lastWorkingDate && (
                  <div className="flex items-start gap-2 mt-1 p-3 bg-amber-50 border border-amber-300 rounded-md text-sm text-amber-800">
                    <Icon
                      icon="material-symbols:warning-outline-rounded"
                      className="w-5 h-5 shrink-0 mt-0.5 text-amber-500"
                    />
                    <span>
                      <strong>Last Working Day is required</strong> to complete
                      this resignation. Your company is configured to use the
                      Last Working Day as the lapsing date for unvested options.
                    </span>
                  </div>
                )}
              {!formik.values.lastWorkingDate &&
                resignationDetails?.pauseAndRevertOnResignationDate !== false &&
                grantSettings?.dateForLapsing !== "lastWorkingDay" && (
                  <Notes text="If LWD is not set then Resignation Date will be considered as Last Working Day" />
                )}
              {employee?.isPausedVesting &&
                formik.values.lastWorkingDate &&
                startOfDay(new Date(formik.values.lastWorkingDate)) <=
                  startOfDay(new Date()) && (
                  <>
                    <Notes text="All unvested options will be lapsed immediately." />
                    <Notes
                      text={`The vesting will only resume until the last working date i.e, ${format(
                        new Date(formik.values.lastWorkingDate),
                        "dd-MM-yyyy"
                      )}.`}
                    />
                  </>
                )}
              {employee?.isPausedVesting &&
                formik.values.lastWorkingDate &&
                startOfDay(new Date(formik.values.lastWorkingDate)) >
                  startOfDay(new Date()) && (
                  <Notes text="Vesting will resume until today. Vesting might get impacted if the last working day is moved to a date before today" />
                )}
              {formik.values.resignationReason && uniquePlanIds.length > 0 && (
                <VStack className="gap-2 mt-2">
                  {uniquePlanIds.map(({ planId, planName }) => (
                    <PlanForfeitureSummary
                      key={planId}
                      planId={planId}
                      planName={planName}
                      separationReason={formik.values.resignationReason}
                      dateOfJoining={props.dateOfJoining}
                      lastDay={
                        formik.values.lastWorkingDate
                          ? parseDateString(formik.values.lastWorkingDate)
                          : formik.values.resignationDate
                          ? parseDateString(formik.values.resignationDate)
                          : undefined
                      }
                      resignationDate={
                        formik.values.resignationDate
                          ? parseDateString(formik.values.resignationDate)
                          : undefined
                      }
                    />
                  ))}
                </VStack>
              )}
            </VStack>
            {willgrantsBePasuedOnResignation && (
              <HStack className="text-xs italic text-gray-500 mt-3">
                Note: Vestings will be paused for all the grants on the
                resignation date.
              </HStack>
            )}
            {((grantsToBeDeleted?.length || 0) > 0 ||
              (withdrawnGrants?.length || 0) > 0 ||
              (!willOfferedGrantsBeDeleted &&
                grantsInOfferedState &&
                grantsInOfferedState?.length > 0) ||
              (revertingData?.length || 0) > 0) && (
              <VStack className="justify-center gap-4 py-2 text-xs italic text-gray-500">
                <HStack className="bg-[#FFF3C7] p-2 rounded-md gap-2 text-[#BD7D00] items-center">
                  <Icon
                    icon={"fe:warning"}
                    color={"#BD7D00"}
                    height={32}
                    width={32}
                  />
                  <p className="justify-center">
                    {revertingData?.length > 0 &&
                      `Please Note - you are trying to do a
                      "Back Dated Resignation. "
                    `}
                    {revertingData?.length === 0
                      ? "The following grants were either not in a granted state or not accepted, and will be actioned as mentioned below."
                      : `The following grants will be affected as these activities
                    have happened after the given ${reversionCutoffLabel}. If you
                    choose to proceed, the following grants will be affected as
                    follows:`}
                  </p>
                </HStack>
                {(withdrawnGrants?.length || 0) > 0 && (
                  <HStack className="items-center gap-4">
                    <SwitchButton
                      value={deleteWithdrawnGrants}
                      label="Delete withdrawn grants?"
                      onClick={() =>
                        setDeleteWithdrawnGrants(!deleteWithdrawnGrants)
                      }
                    />
                    {!deleteWithdrawnGrants && (
                      <span className="text-xs text-gray-500">
                        If disabled, all options in withdrawn grants will be
                        lapsed on completion.
                      </span>
                    )}
                  </HStack>
                )}
                <VStack className="px-4 py-4">
                  <table className="w-full text-sm border border-collapse border-gray-300 table-auto">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-4 py-2 text-left border border-gray-300">
                          Grant Info
                        </th>
                        <th className="px-4 py-2 text-left border border-gray-300">
                          Grant Date
                        </th>
                        <th className="px-4 py-2 text-left border border-gray-300">
                          Effect on Grant
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {revertingData.map((item) => {
                        const grant = grantsData?.find(
                          (g) => g.grantIdentifier === item.identifier
                        );
                        return (
                          <tr
                            key={item.identifier}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-2 border border-gray-300">
                              {item.identifier}
                            </td>
                            <td className="px-4 py-2 border border-gray-300">
                              {formatDisplayDate(
                                grant?.grantDate || new Date()
                              )}
                            </td>
                            <td className="px-4 py-2 border border-gray-300">
                              {parseFloat(item.options.toFixed(4))} vested
                              options will be reverted
                            </td>
                          </tr>
                        );
                      })}
                      {grantsToBeDeleted.map((item) => {
                        const grant = grantsData?.find(
                          (g) => g.grantIdentifier === item.grantIdentifier
                        );
                        return (
                          <tr
                            key={item.grantIdentifier}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-2 border border-gray-300">
                              {item.grantIdentifier}
                            </td>
                            <td className="px-4 py-2 border border-gray-300">
                              {formatDisplayDate(item.grantDate)}
                            </td>
                            <td className="px-4 py-2 border border-gray-300">
                              Grant will be deleted
                            </td>
                          </tr>
                        );
                      })}
                      {!deleteWithdrawnGrants &&
                        withdrawnGrants?.map((item) => (
                          <tr
                            key={item.grantIdentifier}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-2 border border-gray-300">
                              {item.grantIdentifier}
                            </td>
                            <td className="px-4 py-2 border border-gray-300">
                              {formatDisplayDate(item.grantDate)}
                            </td>
                            <td className="px-4 py-2 border border-gray-300">
                              Options will be lapsed on completion
                            </td>
                          </tr>
                        ))}
                      {grantsInOfferedState &&
                        grantsInOfferedState?.length > 0 &&
                        !willOfferedGrantsBeDeleted &&
                        grantsInOfferedState.map((item) => (
                          <tr
                            key={item.grantIdentifier}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-2 border border-gray-300">
                              {item.grantIdentifier}
                            </td>
                            <td className="px-4 py-2 border border-gray-300">
                              {formatDisplayDate(item.grantDate)}
                            </td>
                            <td className="px-4 py-2 border border-gray-300">
                              Grant will be moved to &quot;Cancelled&quot; State
                              and Options will be Lapsed
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </VStack>
                {revertingData && revertingData.length > 0 && (
                  <>
                    <VStack className="items-start gap-2 pt-4">
                      <HStack className="gap-6 justify-items-start">
                        <VStack className="p-1">
                          <Label className="">
                            Please Type <b>Resign</b> exactly to proceed
                          </Label>
                          <p className="text-[#8C8C8C] text-xs">
                            This is done as a security check to ensure any false
                            resignation
                          </p>
                        </VStack>
                        <Input
                          type="text"
                          id="confirmation-input"
                          value={confirmationInput}
                          onChange={(e) => setConfirmationInput(e.target.value)}
                          onPaste={(e) => e.preventDefault()}
                          placeholder="Type 'Resign' to confirm"
                          className="p-2 px-6 text-sm border rounded-md w-72"
                        />
                      </HStack>
                      {confirmationInput !== "Resign" &&
                        confirmationInput.length > 0 && (
                          <p className="text-xs text-red-500">
                            Please type <b>Resign</b> exactly to proceed.
                          </p>
                        )}
                    </VStack>
                    <HStack className="items-center gap-2 pt-4">
                      <input
                        type="checkbox"
                        id="confirmation-checkbox"
                        checked={isConfirmed}
                        onChange={(e) => setIsConfirmed(e.target.checked)}
                      />
                      <label
                        htmlFor="confirmation-checkbox"
                        className="text-sm text-[#464E5F]"
                      >
                        I confirm that I understand the above changes and wish
                        to proceed.
                      </label>
                    </HStack>
                  </>
                )}
              </VStack>
            )}
            <HStack className="justify-between mt-4">
              <ButtonPrimary1 onClick={onClose}>Back</ButtonPrimary1>
              <ButtonPrimary
                type="submit"
                disabled={
                  revertingData &&
                  revertingData.length > 0 &&
                  (!isConfirmed || confirmationInput !== "Resign")
                }
              >
                Next
              </ButtonPrimary>
            </HStack>
          </form>
        </VStack>
      </div>
    </>
  );
}
